import { Fragment } from "react";
import { useDispatch } from "react-redux";
import { Box, Button, Card, Chip, TableCell, TableRow, Typography } from "@mui/material";
import { formatInTimeZone } from "date-fns-tz";

import { Table } from "@APP/components";
import { tableStyles as useStyles } from "@APP/styles";
import { AnonymisationStatus, User } from "@APP/types";
import { commonFormatDate } from "@APP/constants";
import { anonymizeUser } from "@APP/services/api";
import { useAlert, useHandleErrorCodes } from "@APP/hooks";
import { hideLoader, showLoader } from "@APP/redux";
import { formatErrorMessage, getUserTimeZone } from "@APP/utils";

interface OrganizationUsersListProps {
  usersData: User[];
  orgId: string;
  fetchOrganizationData: (orgId: string) => Promise<void>;
}

const OrganizationUsersList = ({
  usersData,
  orgId,
  fetchOrganizationData,
}: OrganizationUsersListProps) => {
  const classes = useStyles();
  const alert = useAlert();
  const dispatch = useDispatch();
  const handleErrorCodes = useHandleErrorCodes();

  const getAnonymisationStatusChip = (status?: AnonymisationStatus) => {
    if (!status) return null;

    const statusConfig: Record<
      Exclude<AnonymisationStatus, null>,
      {
        label: string;
        color: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning";
      }
    > = {
      Pending: { label: "Pending", color: "info" },
      InProgress: { label: "In Progress", color: "warning" },
      Completed: { label: "Completed", color: "success" },
      Successful: { label: "Successful", color: "success" },
      Failed: { label: "Failed", color: "error" },
    };

    const config = statusConfig[status];

    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const handleAnonymize = async (customerId: string, username: string) => {
    alert.open(
      "Confirm Anonymization",
      `Are you sure you want to anonymize user "${username}"? This action will start the anonymization process.`,
      [
        {
          text: "Cancel",
          onClick: () => alert.close(),
        },
        {
          text: "OK",
          onClick: async () => {
            alert.close();
            try {
              dispatch(showLoader());
              await anonymizeUser(orgId, customerId);

              alert.open("Success", "Anonymisation process has been started successfully.", [
                {
                  text: "Okay",
                },
              ]);
              fetchOrganizationData(orgId);
            } catch (error: any) {
              const errorData = error?.response?.data;

              // If no error code exists, show generic message
              if (!errorData?.errorCode) {
                alert.open(
                  "Error",
                  "Unable to start anonymisation process. Please try again or contact support if the issue persists.",
                  [{ text: "Okay" }],
                );
              } else {
                // If error code exists, try to handle it
                const isHandled = handleErrorCodes(errorData.errorCode);

                if (!isHandled) {
                  const errorMessage = formatErrorMessage(
                    error,
                    "Unable to start anonymisation process. Please try again or contact support if the issue persists.",
                  );
                  alert.open("Error", errorMessage, [{ text: "Okay" }]);
                }
              }
            } finally {
              dispatch(hideLoader());
            }
          },
        },
      ],
    );
  };

  const renderHeader = () => (
    <TableRow>
      <TableCell align="left">Username</TableCell>
      <TableCell align="left">Status</TableCell>
      <TableCell align="left">Phone</TableCell>
      <TableCell align="left">Created Date</TableCell>
      <TableCell align="left">Roles</TableCell>
      <TableCell align="left">Anonymisation Status</TableCell>
      <TableCell align="left">Actions</TableCell>
    </TableRow>
  );

  const renderRows = (user: User) => {
    const hasRequiredIds = !!(user.customerId && orgId);
    const isAnonymizable = !user.anonymisationsStatus || user.anonymisationsStatus === "Failed";
    const isButtonDisabled = !hasRequiredIds || !isAnonymizable;

    return (
      <Fragment key={user.id || user.username}>
        <TableRow className={classes.tableRow}>
          <TableCell>{user.username}</TableCell>
          <TableCell>{user.status}</TableCell>
          <TableCell>{user.phone || "-"}</TableCell>
          <TableCell>
            {user.meta?.createdAt
              ? formatInTimeZone(user.meta.createdAt, getUserTimeZone(), commonFormatDate)
              : "-"}
          </TableCell>
          <TableCell>{user.roles?.join(", ") || "-"}</TableCell>
          <TableCell>{getAnonymisationStatusChip(user.anonymisationsStatus)}</TableCell>
          <TableCell>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              disabled={isButtonDisabled}
              onClick={() =>
                hasRequiredIds && user.customerId && handleAnonymize(user.customerId, user.username)
              }
              data-testid={`anonymize-btn-${user.username}`}>
              Anonymize
            </Button>
          </TableCell>
        </TableRow>
      </Fragment>
    );
  };

  const renderEmptyDataRow = () => (
    <TableRow>
      <TableCell colSpan={7} align="center">
        No users found for this organisation.
      </TableCell>
    </TableRow>
  );

  return (
    <Card elevation={12}>
      <Box p={3}>
        <Typography variant="h6" gutterBottom>
          Users
        </Typography>
        <Table
          renderHeader={renderHeader}
          data={usersData || []}
          showPagination={false}
          renderRows={renderRows}
          renderEmptyDataRow={renderEmptyDataRow}
        />
      </Box>
    </Card>
  );
};

export default OrganizationUsersList;
